import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styled, { keyframes } from "styled-components";
import theme from "../../../../../assets/theme";
import { G } from "../styles";
import RedactionLayer from "./RedactionLayer";
import {
  buildRedactedPdf,
  openPdfForRedaction,
  redactedFileName,
} from "./redactionUtils";
import { fileToPdfBytes, isPdfFile } from "./convertToPdf";

const MAX_PAGE_WIDTH = 820;

// Lazily renders one page of the PDF being redacted.
function EditorPage({ pdf, pageNumber, width, fallbackRatio, boxes, onAdd, onDelete }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [ratio, setRatio] = useState(fallbackRatio);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !pdf) return undefined;
    let cancelled = false;
    let renderTask = null;
    pdf.getPage(pageNumber).then((page) => {
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      setRatio(base.height / base.width);
      const dpr = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: (width / base.width) * dpr });
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      renderTask = page.render({
        canvasContext: canvas.getContext("2d"),
        viewport,
      });
      renderTask.promise.catch(() => undefined);
    });
    return () => {
      cancelled = true;
      if (renderTask) renderTask.cancel();
    };
  }, [visible, pdf, pageNumber, width]);

  return (
    <PageWrap ref={wrapRef} style={{ width, height: Math.round(width * ratio) }}>
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />
      <PageNo>{pageNumber}</PageNo>
      <RedactionLayer
        page={pageNumber}
        boxes={boxes}
        editable
        onAdd={onAdd}
        onDelete={onDelete}
      />
    </PageWrap>
  );
}

/**
 * Shown when a single PDF, image or Word file is picked for upload. Lets the
 * user choose between uploading the file as-is or redacting it first; the
 * redacted copy is a new copy-protected PDF with every page flattened, so the
 * hidden text is gone for good and the rest can't be copied, even after OCR.
 * Images and Word files are converted to PDF before editing.
 */
export default function RedactUploadFlow({
  file,
  onUploadOriginal,
  onUploadRedacted,
  onCancel,
}) {
  const [step, setStep] = useState("choose");
  const [pdf, setPdf] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [fallbackRatio, setFallbackRatio] = useState(1.414);
  const [loadError, setLoadError] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [progress, setProgress] = useState(null);
  const [width, setWidth] = useState(() =>
    Math.min(MAX_PAGE_WIDTH, window.innerWidth - 48),
  );
  const bytesRef = useRef(null);

  useEffect(() => {
    const onResize = () =>
      setWidth(Math.min(MAX_PAGE_WIDTH, window.innerWidth - 48));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (step !== "edit" || pdf) return undefined;
    let task = null;
    let cancelled = false;
    fileToPdfBytes(file)
      .then((bytes) => {
        if (cancelled) return null;
        bytesRef.current = bytes;
        task = openPdfForRedaction(bytesRef.current);
        return task.promise;
      })
      .then(async (doc) => {
        if (!doc || cancelled) return;
        const first = await doc.getPage(1);
        const vp = first.getViewport({ scale: 1 });
        setFallbackRatio(vp.height / vp.width);
        setNumPages(doc.numPages);
        setPdf(doc);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || "Could not open this document.");
      });
    return () => {
      cancelled = true;
    };
  }, [step, file, pdf]);

  useEffect(() => () => pdf && pdf.destroy(), [pdf]);

  // Esc: leave the editor (or the dialog) without uploading.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Escape" || progress) return;
      e.stopImmediatePropagation();
      onCancel();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [onCancel, progress]);

  const addBoxes = (list) => setBoxes((prev) => [...prev, ...list]);
  const deleteBox = (id) => setBoxes((prev) => prev.filter((b) => b.id !== id));

  const apply = async () => {
    if (!boxes.length) return;
    setProgress({ done: 0, total: numPages });
    try {
      const blob = await buildRedactedPdf(bytesRef.current, boxes, {
        onProgress: (done, total) => setProgress({ done, total }),
      });
      const redacted = new File([blob], redactedFileName(file.name), {
        type: "application/pdf",
      });
      await onUploadRedacted(redacted);
    } catch (err) {
      setLoadError(err?.message || "Could not redact this document.");
      setProgress(null);
    }
  };

  const content =
    step === "choose" ? (
      <Backdrop onClick={onCancel}>
        <ChoiceCard onClick={(e) => e.stopPropagation()}>
          <ChoiceTitle>Upload this document</ChoiceTitle>
          <ChoiceFile title={file.name}>{file.name}</ChoiceFile>
          <ChoiceOption type="button" onClick={() => setStep("edit")}>
            <OptionIcon $dark>
              <RedactIcon />
            </OptionIcon>
            <div>
              <OptionTitle>Redact &amp; Upload</OptionTitle>
              <OptionSub>
                Black out confidential text first. Only the redacted copy is
                uploaded, as a PDF that can't be copied from.
              </OptionSub>
            </div>
          </ChoiceOption>
          <ChoiceOption type="button" onClick={() => onUploadOriginal(file)}>
            <OptionIcon>
              <UploadIcon />
            </OptionIcon>
            <div>
              <OptionTitle>Upload as is</OptionTitle>
              <OptionSub>Upload the original file without changes.</OptionSub>
            </div>
          </ChoiceOption>
          <ChoiceCancel type="button" onClick={onCancel}>
            Cancel
          </ChoiceCancel>
        </ChoiceCard>
      </Backdrop>
    ) : (
      <EditorShell>
        <EditorBar>
          <EditorTitleGroup>
            <EditorTitle>Redact before upload</EditorTitle>
            <EditorSub title={file.name}>
              {file.name}
              {numPages ? ` · ${numPages} page${numPages > 1 ? "s" : ""}` : ""}
            </EditorSub>
          </EditorTitleGroup>
          <EditorActions>
            <Count>
              {boxes.length} redaction{boxes.length === 1 ? "" : "s"}
            </Count>
            <GhostBtn
              type="button"
              disabled={!boxes.length || !!progress}
              onClick={() => setBoxes((prev) => prev.slice(0, -1))}
            >
              Undo
            </GhostBtn>
            <GhostBtn
              type="button"
              disabled={!boxes.length || !!progress}
              onClick={() => setBoxes([])}
            >
              Clear all
            </GhostBtn>
            <GhostBtn type="button" disabled={!!progress} onClick={onCancel}>
              Cancel
            </GhostBtn>
            <ApplyBtn
              type="button"
              disabled={!boxes.length || !!progress || !pdf}
              onClick={apply}
            >
              {progress
                ? `Redacting… ${progress.done}/${progress.total}`
                : "Apply & Upload"}
            </ApplyBtn>
          </EditorActions>
        </EditorBar>
        <Hint>
          Drag over any text or area to black it out. The uploaded copy is
          flattened and copy-protected, so no text in it can be copied or
          recovered.
        </Hint>
        <EditorScroll>
          {loadError && <ErrorText>{loadError}</ErrorText>}
          {!pdf && !loadError && (
            <LoadingText>
              {isPdfFile(file)
                ? "Opening document…"
                : "Converting to PDF for redaction…"}
            </LoadingText>
          )}
          {pdf &&
            Array.from({ length: numPages }, (_, i) => (
              <EditorPage
                key={i + 1}
                pdf={pdf}
                pageNumber={i + 1}
                width={width}
                fallbackRatio={fallbackRatio}
                boxes={boxes.filter((b) => b.page === i + 1)}
                onAdd={addBoxes}
                onDelete={deleteBox}
              />
            ))}
        </EditorScroll>
      </EditorShell>
    );

  return ReactDOM.createPortal(content, document.body);
}

const RedactIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="4" rx="1" fill="currentColor" />
    <line x1="3" y1="13" x2="21" y2="13" />
    <rect x="3" y="16" width="11" height="3" rx="1" fill="currentColor" />
  </svg>
);

const UploadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Above Material-UI modals (z-index 1300), since the upload dialog is one.
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: rgba(26, 35, 56, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: ${fadeIn} 0.15s ease;
`;

const ChoiceCard = styled.div`
  background: ${G.white};
  border-radius: 14px;
  box-shadow: ${G.shadowLg};
  width: min(440px, 100%);
  padding: 22px 22px 16px;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const ChoiceTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: ${G.navy};
  font-family: ${theme.fonts.primaryFontExtraBold};
`;

const ChoiceFile = styled.div`
  margin: 4px 0 16px;
  font-size: 12px;
  color: ${G.slate500};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChoiceOption = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 14px;
  margin-bottom: 10px;
  border: 1.5px solid ${G.slate200};
  border-radius: 10px;
  background: ${G.white};
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
  &:hover {
    border-color: ${G.teal};
    background: ${G.tealGhost};
  }
`;

const OptionIcon = styled.div`
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $dark }) => ($dark ? G.slate800 : G.tealLight)};
  color: ${({ $dark }) => ($dark ? G.white : G.teal)};
`;

const OptionTitle = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${G.slate800};
`;

const OptionSub = styled.div`
  font-size: 12px;
  color: ${G.slate500};
  margin-top: 2px;
  line-height: 1.45;
`;

const ChoiceCancel = styled.button`
  display: block;
  margin: 4px auto 0;
  padding: 6px 14px;
  border: none;
  background: none;
  color: ${G.slate500};
  font-size: 12.5px;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    color: ${G.slate800};
  }
`;

const EditorShell = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: #eef1f5;
  display: flex;
  flex-direction: column;
  font-family: ${theme.fonts.primaryFontRegular};
  animation: ${fadeIn} 0.15s ease;
`;

const EditorBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 18px;
  background: ${G.white};
  border-bottom: 1px solid ${G.slate200};
`;

const EditorTitleGroup = styled.div`
  min-width: 0;
`;

const EditorTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${G.navy};
`;

const EditorSub = styled.div`
  font-size: 11.5px;
  color: ${G.slate500};
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EditorActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Count = styled.span`
  font-size: 12px;
  color: ${G.slate600};
  margin-right: 4px;
`;

const GhostBtn = styled.button`
  height: 32px;
  padding: 0 12px;
  border-radius: 7px;
  border: 1px solid ${G.slate200};
  background: ${G.white};
  color: ${G.slate700};
  font-size: 12.5px;
  cursor: pointer;
  font-family: inherit;
  &:hover:not(:disabled) {
    border-color: ${G.teal};
    color: ${G.teal};
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const ApplyBtn = styled.button`
  height: 32px;
  padding: 0 16px;
  border-radius: 7px;
  border: none;
  background: ${G.slate800};
  color: ${G.white};
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  &:hover:not(:disabled) {
    background: #000000;
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const Hint = styled.div`
  padding: 7px 18px;
  font-size: 12px;
  color: #7f1d1d;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
`;

const EditorScroll = styled.div`
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  padding: 20px 24px 40px;
`;

const PageWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  background: ${G.white};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
`;

const PageNo = styled.span`
  position: absolute;
  left: -2px;
  bottom: -18px;
  font-size: 11px;
  color: ${G.slate500};
`;

const LoadingText = styled.div`
  margin-top: 40px;
  font-size: 13px;
  color: ${G.slate500};
`;

const ErrorText = styled.div`
  margin-top: 40px;
  font-size: 13px;
  color: ${G.redDark};
`;
